// Turning a lead sheet into a PERFORMANCE: head → solos → head out.
//
// `analyzeForm` reads what the sheet encodes (pickup / chorus / coda). This
// module supplies the half the sheet does not: the itinerary. A survey of the
// bundled Jazz 1460 found the itinerary written down almost never — 102 charts
// carry any staff text at all, exactly one says "Take coda 2nd time after
// solos" and one "Coda last time only". Those two are simply spelling out what
// every player already does, which is the argument for making the convention
// the DEFAULT behaviour rather than something parsed out of guide text.
//
// Repeat semantics, per the user's rulings (July 2026, revised):
//   · the HEAD IN plays the strain AS WRITTEN — a bare repeat around the
//     chorus means the head goes round twice (500 Miles High's source even
//     carries two lyric verses for the two passes);
//   · a SOLO chorus is one pass of the form — the repeat structures the head,
//     not the soloist's count;
//   · the HEAD OUT is one pass (repeats are not taken on a D.S. by
//     convention), leaving at the To Coda into the coda;
//   · a repeat inside the coda is LITERAL — you really do play the tag twice
//     (which is what a "2nd time, rit." marking is telling you).
// Endings are written form either way: a chorus carrying 1st/2nd brackets
// expands literally and counts as ONE head pass (its passes differ).

import { analyzeForm, type FormAnalysis } from './form';
import {
  bareOuterRepeat,
  cutAtEndMarker,
  flattenChart,
  flattenMeasures,
  honoursEndMarker,
  passageMeasures,
  planPlaybackForm,
} from './ireal/flatten';
import type { IRealChart, IRealMeasure } from './ireal/types';

/** How to turn the sheet into a performance. */
export type ArrangementPolicy =
  /** Obey the notation exactly once — the literal reading, for reading practice. */
  | 'as-written'
  /** The jazz itinerary: head, solo choruses, head out taking the coda. */
  | 'head-solos-head';

export type PassKind = 'head' | 'solo' | 'head-out' | 'written';

export interface PerformanceOptions {
  policy?: ArrangementPolicy;
  /**
   * Number of SOLO choruses between the head in and the head out. The head
   * and the out are implied: total passes = written head passes + solos + 1.
   */
  solos?: number;
}

export interface PerformancePlan {
  /**
   * Every measure in performance order, ready for playback. Melody is STRIPPED
   * from the passes that shouldn't sound it, so melody-track builders need no
   * per-pass logic — a solo chorus simply carries no notes.
   */
  measures: IRealMeasure[];
  /** Index into `measures` where each pass begins. */
  passStartIndices: number[];
  passKinds: PassKind[];
  form: FormAnalysis;
  policy: ArrangementPolicy;
}

const NAV_RE = /D\.\s*[SC]\./i;

/** Drop navigation furniture: under a policy, the plan supplies the itinerary,
 * so a leftover D.S. would send `flattenMeasures` round the form again. */
function stripNav(measure: IRealMeasure): IRealMeasure {
  const out: IRealMeasure = { ...measure };
  delete out.segno;
  delete out.coda;
  if (out.directive && NAV_RE.test(out.directive)) delete out.directive;
  return out;
}

function withoutMelody(measure: IRealMeasure): IRealMeasure {
  if (!measure.melody) return measure;
  const out = { ...measure };
  delete out.melody;
  return out;
}

/** Resolve inherited harmony, similes and meter before cutting a written region. */
function contextualSpan(chart: IRealChart, start: number, end: number): IRealMeasure[] {
  const bars = passageMeasures(chart.measures, start, end, chart.timeSignature);
  if (!bars.length) return bars;
  let meter = chart.timeSignature;
  for (let i = 0; i <= start; i += 1) meter = chart.measures[i]?.timeSig ?? meter;
  return [{ ...bars[0], timeSig: meter }, ...bars.slice(1)];
}

/** A return starts in its written context, not the last solo's meter/chord. */
function expandReturn(chart: IRealChart, navigationIndex: number): IRealMeasure[] {
  const bars = flattenMeasures(chart.measures, { returnFrom: navigationIndex }).map(stripNav);
  if (!bars.length) return bars;
  const start = chart.measures.findIndex((m) => m.id === bars[0].id);
  if (start < 0) return bars;
  const entry = contextualSpan(chart, start, start)[0];
  bars[0] = {
    ...bars[0],
    timeSig: entry.timeSig,
    ...(bars[0].chords.length ? {} : { chords: entry.chords }),
  };
  return bars;
}

/**
 * Expand a written span into playable bars. `collapseOuterRepeat` applies the
 * chorus rule — a bare repeat around the span DECLARES the form, so it is
 * dropped rather than doubling the pass. The coda passes false: its repeat is
 * literal (that is what a "2nd time, rit." marking is counting). Internal
 * repeats always expand, as does any span carrying endings — 1st/2nd-ending
 * brackets are written form, not a loop marker.
 */
function expandSpan(
  chart: IRealChart,
  start: number,
  end: number,
  collapseOuterRepeat: boolean,
): IRealMeasure[] {
  const bars = contextualSpan(chart, start, end).map(stripNav);
  if (bars.length === 0) return [];
  if (collapseOuterRepeat && bareOuterRepeat(bars)) {
    if (bars[0].open === 'repeat') bars[0] = { ...bars[0], open: undefined };
    const last = bars.length - 1;
    if (bars[last].close === 'repeat') bars[last] = { ...bars[last], close: undefined };
  }
  return flattenMeasures(bars);
}

/** One chorus: what a solo pass plays, and what the head is written over. */
function expandChorus(chart: IRealChart, form: FormAnalysis): IRealMeasure[] {
  return expandSpan(chart, form.chorus.start, form.chorus.end, true);
}

/**
 * How many passes the WRITTEN head makes: a bare repeat around the chorus
 * sends the head round twice (an `Nx` staff text overrides the count, the
 * same channel flatten's passesFor reads). Endings mean the expansion is one
 * logical head pass — its two voltas differ, so it cannot split into equal
 * passes.
 */
function writtenHeadPasses(chart: IRealChart, form: FormAnalysis): number {
  const bars = chart.measures.slice(form.chorus.start, form.chorus.end + 1);
  if (bars.length === 0 || bars.some((m) => m.ending != null)) return 1;
  // The same pairing rule expandSpan collapses by: an opening |: that closes
  // mid-span is an interior vamp, not the whole-head repeat.
  if (!bareOuterRepeat(bars)) return 1;
  if (bars[0].open !== 'repeat' || bars[bars.length - 1].close !== 'repeat') return 1;
  for (const m of bars) {
    const nx = /(\d+)x/.exec(m.staffText ?? '');
    if (nx) return Math.max(2, parseInt(nx[1], 10));
  }
  return 2;
}

/** The head out: the chorus up to (not including) the To Coda bar — the sign
 * sits at its start, so the coda replaces it — then the coda itself (whose
 * own repeats are taken literally). */
function expandHeadOut(chart: IRealChart, form: FormAnalysis): IRealMeasure[] {
  const upToExit =
    form.codaExit != null && form.codaExit > form.chorus.start
      ? expandSpan(chart, form.chorus.start, form.codaExit - 1, true)
      : expandChorus(chart, form);
  const coda = form.coda ? expandSpan(chart, form.coda.start, form.coda.end, false) : [];
  return [...upToExit, ...coda];
}

/**
 * Build the performance. `as-written` returns the literal reading unchanged
 * (`flattenChart`); `head-solos-head` returns the written head (its repeat
 * honoured) + N solo choruses + one head out taking the coda, with the
 * melody present only on the head passes.
 */
export function buildPerformance(
  chart: IRealChart,
  options: PerformanceOptions = {},
): PerformancePlan {
  const policy = options.policy ?? 'as-written';
  const form = analyzeForm(chart);

  const literal = (): PerformancePlan => ({
    measures: flattenChart(chart),
    passStartIndices: [0],
    passKinds: ['written'],
    form,
    policy: 'as-written',
  });
  if (policy === 'as-written') return literal();

  const chorus = expandChorus(chart, form);
  let headOut = expandHeadOut(chart, form);
  /** What the head-in passes play — the chorus, plus a leading intro. */
  let headBars = chorus;
  /** What one solo pass plays. */
  let soloChorus = chorus;
  let headPasses = writtenHeadPasses(chart, form);
  /**
   * Entry material (an intro or verse): played ONCE, before the first head
   * pass — its own repeats taken literally, because an intro vamp really does
   * play its written count. Solo choruses and the head out never restate it,
   * which is the whole reason `analyzeForm` keeps it out of the chorus.
   */
  let entryIntro = form.intro
    ? expandSpan(chart, form.intro.start, form.intro.end, false)
    : ([] as IRealMeasure[]);

  const referencedReturn = form.afterSolos ?? -1;

  // WRITTEN ITINERARY (section roles): the chart wrote its solo section down
  // ("Solos x4" staff text, a Solo section, or an explicit sectionRole).
  // Each region flattens with its OWN navigation intact — Moanin' writes its
  // solo AABA in full with its own segno/Fine — and the marks strip from the
  // RESULT, never before (stripping first would gut the region's form):
  //   · head in  = the literal reading of everything before the region;
  //   · a solo   = ONE pass of the region (a bare outer repeat / Nx marking
  //                declares the loop — the chip counts choruses);
  //   · head out = the head to its ⊕ exit, then the coda; with no exit, the
  //                head again plus any post-region coda.
  if (referencedReturn >= 0 && !form.solos) {
    // "After solos, D.S." is an exit instruction, not compression of one
    // chorus. State the written head through its break, solo on the named
    // music, then let the same navigation interpreter read the final return.
    const beforeReturn = chart.measures.slice(0, referencedReturn + 1).map((m, i) =>
      i === referencedReturn ? { ...m, directive: undefined } : m,
    );
    headBars = flattenMeasures(beforeReturn).slice(form.pickup ? 1 : 0).map(stripNav);
    entryIntro = [];
    headOut = expandReturn(chart, referencedReturn);
    headPasses = 1;
  } else if (form.solos) {
    const sl = form.solos;
    const ms = chart.measures;
    // A directive is EXTERNAL to a slice when its jump target lies outside
    // it: Moanin's head ends "D.C. al Fine" resolving within the head (keep
    // — it IS the theme's AABA compression), while The Intrepid Fox's head
    // ends "D.C. al Coda" whose ⊕ target is the solo vamp (strip — that
    // jump belongs to the final pass, not to every head or solo chorus).
    const segnoAt = ms.findIndex((m) => m.segno);
    const codaTarget = ms.reduce((last, m, i) => (m.coda ? i : last), -1);
    const inside = (i: number, lo: number, hi: number) => i >= lo && i <= hi;
    const sliceFor = (lo: number, hi: number) =>
      contextualSpan(chart, lo, hi).map((m) => {
        if (!m.directive || !/D\.\s*[SC]\./i.test(m.directive)) return { ...m };
        const wantsCoda = /coda/i.test(m.directive);
        const wantsFine = /fine/i.test(m.directive);
        const fineAt = ms.findIndex((bar, i) => inside(i, lo, hi) && /^fine/i.test(bar.directive ?? ''));
        const returnTo = /D\.\s*S\./i.test(m.directive) ? segnoAt : lo;
        const external =
          !inside(returnTo, lo, hi) ||
          (wantsCoda && !inside(codaTarget, lo, hi)) ||
          (wantsFine && !inside(fineAt, lo, hi));
        return external ? { ...m, directive: undefined } : { ...m };
      });

    // The head is the pre-region material AFTER the intro — the intro is
    // entry material prepended to the first pass alone, so the head out and
    // any repeated head pass never restate it.
    const bodyStart = form.intro ? form.intro.end + 1 : 0;
    headBars =
      sl.start > bodyStart ? flattenMeasures(sliceFor(bodyStart, sl.start - 1)).map(stripNav) : [];
    const region = sliceFor(sl.start, sl.end);
    if (
      bareOuterRepeat(region) &&
      region[0].open === 'repeat' &&
      region[region.length - 1].close === 'repeat'
    ) {
      // A bare repeat (with an Nx marking) around the region DECLARES the
      // loop — one solo chorus is one pass; the chip counts choruses.
      region[0] = { ...region[0], open: undefined };
      region[region.length - 1] = { ...region[region.length - 1], close: undefined, staffText: undefined };
    }
    soloChorus = flattenMeasures(region).map(stripNav);
    // The head out: to the ⊕ exit, then the coda — which may BE the solo
    // region itself (The Intrepid Fox's solo vamp doubles as its ⊕ target).
    const codaIsRegion = ms[sl.start]?.coda === true;
    const codaBars = form.coda ? ms.slice(form.coda.start, form.coda.end + 1) : [];
    if (form.codaExit != null && form.codaExit > bodyStart) {
      // Out to the ⊕ is the D.S./D.C. return, and a return takes no repeats
      // and plays only the last ending (Un Poco Loco's |:A:| once, not twice).
      headOut = [
        ...flattenMeasures(sliceFor(bodyStart, form.codaExit - 1), { asReturn: true }).map(
          stripNav,
        ),
        ...(codaIsRegion ? soloChorus : flattenMeasures(codaBars).map(stripNav)),
      ];
    } else {
      headOut = [
        ...headBars,
        ...(form.coda && form.coda.start > sl.end ? flattenMeasures(codaBars).map(stripNav) : []),
      ];
    }
    // A return written at the end of the solo section can target a Fine or
    // coda back in the head. It must not replay the head's ordinary repeats.
    const returnAt = ms.findIndex((m, i) => i >= sl.start && i <= sl.end && NAV_RE.test(m.directive ?? ''));
    if (returnAt >= 0) {
      const returnTo = /D\.\s*S\./i.test(ms[returnAt].directive ?? '') ? segnoAt : 0;
      if (returnTo < sl.start) {
        headOut = expandReturn(chart, returnAt);
      }
    }
    headPasses = 1; // the page writes everything out — nothing is implied
  } else {
    // COMPRESSION FORMS (user ruling July 2026, option c): when a D.C./D.S.
  // compresses one chorus onto the page — Body And Soul's |:A:| B + D.C. al
  // Coda IS a single 32-bar AABA — the chorus is the navigation READING, not
  // the written span (which would give solos of A-A-B, missing the last A).
  // A tune whose bare repeat wraps the WHOLE chorus keeps the classic shape
  // (500 Miles High: the strain repeats, the D.S. is the out mechanism).
    const hasNav = chart.measures.some((m) => NAV_RE.test(m.directive ?? ''));
    if (hasNav && headPasses === 1) {
      // The statement is the WHOLE page's navigation reading, any intro
      // included — a D.C. really does return to the written top — so the
      // separate entry prepend would double it.
      entryIntro = [];
      const statement = flattenChart(chart)
        .slice(form.pickup ? 1 : 0)
        .map(stripNav);
      headBars = statement;
      soloChorus = statement;
      headOut = statement;
      const alFine = chart.measures.some((m) => /D\.\s*[SC]\..*fine/i.test(m.directive ?? ''));
      if (alFine) {
        // The al-Fine return is terminal material: solos play the form
        // straight; only the head out takes the da capo and stops at Fine.
        soloChorus = planPlaybackForm(chart.measures)
          .loopPass.slice(form.pickup ? 1 : 0)
          .map(stripNav);
        headBars = soloChorus;
      } else if (form.coda && form.codaExit == null) {
        // An APPENDED coda — no ⊕ exit, entered by falling through (Waltz For
        // Debby) — is an ending, so solos stop where it begins. A coda WITH an
        // exit completes the form (Body And Soul's 1-bar tag is bar 32 of the
        // AABA; I'll Remember April's coda is the last A's back half) and
        // stays in every pass.
        const codaIds = new Set(
          chart.measures.slice(form.coda.start, form.coda.end + 1).map((m) => m.id),
        );
        let cut = statement.length;
        while (cut > 0 && codaIds.has(statement[cut - 1].id)) cut -= 1;
        soloChorus = statement.slice(0, cut);
        headBars = soloChorus;
      }
    }
  }

  // A named solo instruction also applies to charts with no D.S./D.C.
  // A separately written solo region remains authoritative when present.
  const ref = form.soloReference;
  if (ref && !form.solos) {
    if (ref.takeRepeat) {
      const loop = contextualSpan(chart, ref.start, ref.end).map(stripNav);
      loop[0] = { ...loop[0], open: undefined };
      loop[loop.length - 1] = { ...loop[loop.length - 1], close: undefined };
      soloChorus = flattenMeasures(loop);
    } else {
      soloChorus = expandSpan(chart, ref.start, ref.end, true);
    }
  }

  // A chart with no readable chorus (empty or degenerate) has no itinerary to
  // impose; fall back to the notation rather than inventing one.
  if (headBars.length === 0 || soloChorus.length === 0) return literal();

  const pickup = form.pickup
    ? chart.measures.slice(form.pickup.start, form.pickup.end + 1).map(stripNav)
    : [];

  const measures: IRealMeasure[] = [];
  const passStartIndices: number[] = [];
  const passKinds: PassKind[] = [];
  // A written ensemble interlude inside a solo form still sounds its notes.
  // Its instruction lasts to the next section, rather than just its first
  // bar; ordinary solo sections continue to leave room for improvisation.
  const writtenSoloBars = new Set<string>();
  let playWritten = false;
  for (const bar of chart.measures) {
    if (bar.section || bar.sectionRole || bar.coda) playWritten = false;
    if (/\bas\s+written\s+(?:each|every)\s+(?:x|time)\b/i.test(bar.staffText ?? '')) {
      playWritten = true;
    }
    if (playWritten) writtenSoloBars.add(bar.id);
  }
  const addPass = (bars: IRealMeasure[], kind: PassKind, melody: boolean) => {
    passStartIndices.push(measures.length);
    passKinds.push(kind);
    measures.push(...(melody ? bars : bars.map((bar) =>
      writtenSoloBars.has(bar.id) ? bar : withoutMelody(bar),
    )));
  };

  // The pickup and the intro lead into the first head pass only. The head in
  // plays the strain as written (twice under a bare repeat), the solos are
  // one chorus each, and the head out is a single pass taking the coda.
  const solos = Math.max(0, Math.round(options.solos ?? 1));
  addPass([...pickup, ...entryIntro, ...headBars], 'head', true);
  for (let i = 1; i < headPasses; i += 1) addPass(headBars, 'head', true);
  for (let i = 0; i < solos; i += 1) addPass(soloChorus, 'solo', false);
  // iReal's `U` end marker: the head out is the way OUT, so it stops at the
  // marker; the heads in and the solos play the written material past it.
  addPass(honoursEndMarker(chart.measures) ? cutAtEndMarker(headOut) : headOut, 'head-out', true);

  return { measures, passStartIndices, passKinds, form, policy };
}
